import React, { PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';
import { Autocomplete, CircularProgress, FormControl, TextField } from '@mui/material';
import ValidationContext from '../../../../hooks/UseValidation/validation.context';
import { QueryKey, useInfiniteQuery, useQuery, useQueryClient } from 'react-query';
import { InputControlProps } from '../input.control.props';
import { useTranslation } from 'react-i18next';
import { useValidation } from '../../../../hooks/UseValidation';
import useDebounce from '../../../../hooks/UseDebounce';
import { AutocompleteResponse, SelectItem } from '../../../../model';
import { useErrorHandler } from '../../../../hooks/UseErrorHandler';
import { DataTypeQueryKey, EntityQueryKey } from '../../../../query/query.keys';
import { AutocompleteService } from '../../../../api/autocomplete.service';
import { uniqBy } from 'lodash';

interface IProps<T> extends InputControlProps<T> {
  entity: EntityQueryKey;
  options?: (queryKey: EntityQueryKey, term: string | (string | number)[], skip?: number) => Promise<AutocompleteResponse>;
  all?: boolean;
  auxQueryKey?: QueryKey;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: SelectItem,
  ) => React.ReactNode;
  getOptionLabel?: (option: SelectItem) => string;
}

interface IPropsSingle extends IProps<number | string | null> {
  multiple?: false;
  value: number | string | null;
}

interface IPropsMulti extends IProps<number[] | string[] | null> {
  multiple: true;
  value: number[] | string[];
}

export default function AutocompleteControl(props: PropsWithChildren<IPropsSingle | IPropsMulti>) {
  const { t } = useTranslation();
  const validate = useValidation(props.validators);
  const { validateOn } = useContext(ValidationContext);
  const errorHandler = useErrorHandler();

  const [inputValue, setInputValue] = useState('');
  const searchTerm = useDebounce(inputValue, 200);
  const [selected, setSelected] = useState<SelectItem | SelectItem[] | null>(props.multiple ? [] : null);
  const [errorText, setErrorText] = useState<string>();
  const [pending, setPending] = useState(true);
  const [options, setOptions] = useState<SelectItem[]>([]);

  const optionsFn = props.options ?? AutocompleteService.fetchFn;

  const hasValue = useCallback((val: any) => Array.isArray(val) ? val.length > 0 : (val != null && val != ''), []);

  const queryClient = useQueryClient();
  const { data: initialValue } = useQuery(
    [DataTypeQueryKey.Autocomplete, props.entity, props.multiple ? props.value : String(props.value ?? ''), props.auxQueryKey],
    () => (props.value ? optionsFn(props.entity, new Array<number | string>().concat(props.value)) : Promise.resolve(null)),
    {
      select: (data) => (data?.items && props.multiple ? data.items : data?.items?.[0] ?? null),
      onSuccess: (data) => {
        if ((data === null || (Array.isArray(data) && data.length === 0)) && hasValue(props.value)) {
          if (props.multiple) {
            props.onChange([]);
          }
          if (!props.multiple) {
            props.onChange(null);
          }
        }
      },
      onError: () => {
        setSelected(props.multiple ? [] : null);
      },
      enabled: props.auxQueryKey === undefined || new Array<QueryKey>().concat(props.auxQueryKey).every((qk) => qk !== undefined),
    },
  );
  useEffect(() => {
    setSelected(initialValue ?? (props.multiple ? [] : null));
    if (initialValue) {
      props.onChange(
        Array.isArray(initialValue) ? initialValue.map((nv) => nv.value) : (initialValue?.value as any),
        initialValue,
      );
    }
  }, [initialValue]);

  const fetchItems = ({ pageParam }: any) => {
    return optionsFn(props.entity, searchTerm, pageParam);
  };

  const {
    data: filterOptions,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery([DataTypeQueryKey.Autocomplete, props.entity, 'options', searchTerm.trim(), props.auxQueryKey], (params) => fetchItems(params), {
    getNextPageParam: (lastPage) => {
      if (props.all) return false;
      return lastPage.nextOffset;
    },
    select: (data) => {
      const items = data?.pages.flatMap((d) => d?.items ?? []) ?? [];
      if (selected && items.length < 1 && hasValue(props.value)) {
        props.onChange(null);
      }
      return {
        pages: items,
        pageParams: [items.length],
      };
    },
    onError: (err) => {
      errorHandler(`Async select load failed`, err);
      setSelected(props.multiple ? [] : null);
    },
    enabled: !pending,
    keepPreviousData: false,
  });

  useEffect(() => {
    if (filterOptions === undefined) return;

    let selectedOptions: SelectItem[] = [];
    if (selected != null) {
      selectedOptions = new Array<SelectItem>().concat(selected);
    }
    setOptions(() => uniqBy([...selectedOptions, ...(filterOptions.pages ?? [])], 'value'));
  }, [filterOptions]);

  useEffect(() => {
    setErrorText(props.error ? t(props.error) : validate(props.value, props.tab ?? 'main'));
  }, [props.value, props.error, validateOn, props.validators]);

  useEffect(() => () => {
    queryClient.removeQueries({
      queryKey: [DataTypeQueryKey.Autocomplete, props.entity],
      exact: false,
    });
  }, []);

  return (
    <FormControl required={props.required} error={!!errorText} fullWidth size="small" margin="dense">
      <Autocomplete
        autoComplete
        value={selected}
        filterOptions={(x) => x}
        filterSelectedOptions
        onChange={(event: React.SyntheticEvent, newValue: SelectItem | SelectItem[] | null) => {
          if (newValue != null && (!Array.isArray(newValue) || newValue.length)) {
            queryClient.setQueryData(
              [DataTypeQueryKey.Autocomplete, props.entity, Array.isArray(newValue) ? newValue.map((x) => x.value) : String(newValue.value)],
              { items: new Array<SelectItem>().concat(newValue), nextOffset: false }
            );
            if (!Array.isArray(newValue)) {
              queryClient.setQueryData(
                [props.entity, newValue.text, 'autocomplete', props.auxQueryKey],
                { pages: [{items: new Array<SelectItem>().concat(newValue), nextOffset: false }] }
              );
            }
          }
          if (props.multiple && Array.isArray(newValue)) {
            props.onChange(newValue?.map((nv) => nv.value) as any, newValue);
            return;
          }
          if (!props.multiple && !Array.isArray(newValue)) {
            props.onChange(newValue?.value ?? null, newValue);
            return;
          }
          props.onChange(null);
        }}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setPending(!event);
          setInputValue(newInputValue);
        }}
        isOptionEqualToValue={(option, selectValue) => option.value === selectValue.value}
        getOptionLabel={props?.getOptionLabel ?? ((option) => option.text)}
        options={options}
        loading={isFetching || isFetchingNextPage}
        multiple={props.multiple}
        disabled={props.disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t(props.labelKey)}
            required={props.required}
            error={!!errorText}
            helperText={errorText}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {isFetching || isFetchingNextPage ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
            onFocus={() => setPending(false)}
          />
        )}
        renderOption={props.renderOption ?? ((props, option) => {
          return (
            <li {...props} key={option.value}>
              {option.text}
            </li>
          );
        })}
        ListboxProps={{
          onScroll: (event: React.SyntheticEvent) => {
            const listboxNode = event.currentTarget;
            if (listboxNode.scrollTop + listboxNode.clientHeight === listboxNode.scrollHeight) {
              if (hasNextPage && !isFetchingNextPage) {
                return fetchNextPage();
              }
            }
          },
        }}
        
      />
      {props.children}
    </FormControl>
  );
}
